// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomFieldDefinition } from "@eisinc/CustomFieldDefinition";
import LazyImage, { LazyImageProps } from "@eisinc/MobiusOverrides/LazyImage";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Clickable from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    brandsToDisplay = "brandsToDisplay",
    numberOfBrandsToDisplay = "numberOfBrandsToDisplay",
    brandsSelected = "brandsSelected",
    maximumNumberOfBrandsPerRow = "maximumNumberOfBrandsPerRow",
    maximumBrandImageHeight = "maximumBrandImageHeight",
    uppercaseTitle = "uppercaseTitle",
    boldTitle = "boldTitle",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.brandsToDisplay]: "random" | "select-brands";
        [fields.numberOfBrandsToDisplay]: string;
        [fields.brandsSelected]: string[];
        [fields.maximumNumberOfBrandsPerRow]: string;
        [fields.maximumBrandImageHeight]: number;
        [fields.uppercaseTitle]: boolean;
        [fields.boldTitle]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const brandGalleryParameter = {
        brandIds: props.fields.brandsSelected,
        randomize: props.fields.brandsToDisplay === "random",
        pageSize: parseInt(props.fields.numberOfBrandsToDisplay || "0", 10),
        select: "id,name,detailPagePath,logoSmallImagePath,logoAltText",
    };
    const brandsState = getBrandsDataView(state, brandGalleryParameter);
    const shouldLoadBrandGallery = !brandsState.isLoading && !brandsState.value;

    return {
        brandsState,
        shouldLoadBrandGallery,
        brandGalleryParameter,
    };
};

const mapDispatchToProps = {
    loadBrandGallery: loadBrands,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface BrandListStyles {
    titleText?: TypographyProps;
    container?: InjectableCss;
    image?: LazyImageProps;
    imageContainer?: GridContainerProps;
    imageItem?: GridItemProps;
    link?: LinkProps;
    skipToContent?: SkipNavStyles;
    // XNG-Changes
    title?: TypographyProps;
    titleViewAll?: InjectableCss;
}

export const listStyles: BrandListStyles = {
    titleText: {
        variant: "h3",
        transform: "inherit",
        css: css`
            border-width: 20px;
            text-align: center;
        `,
    },
    imageItem: {
        css: css`
            padding: 5px;
            justify-content: center;
        `,
    },
};

const styles = listStyles;

class BrandGallery extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const { loadBrandGallery, shouldLoadBrandGallery, brandGalleryParameter } = this.props;

        if (shouldLoadBrandGallery) {
            loadBrandGallery(brandGalleryParameter);
        }
    }

    render() {
        const {
            brandsState: { isLoading, value: brandList },
            fields,
        } = this.props;

        if (isLoading || !brandList) {
            return null;
        }
        const desktop = parseInt(fields.maximumNumberOfBrandsPerRow, 10);
        const tablet = Math.ceil(desktop / 2);
        const mobile = Math.ceil(tablet / 2);

        const imgProps = {
            style: {
                width: "auto",
                maxWidth: "100%",
                // XNG-Changes
                maxHeight: `min(${fields.maximumBrandImageHeight}px, 100%)`,
            },
        };

        const focusFunction = () => {
            const elementToFocus = document.getElementById("collapseButton");
            if (elementToFocus) {
                elementToFocus.focus();
            } else {
                document.getElementById("expandButton")?.focus();
            }
        };

        return (
            <StyledWrapper data-test-selector="brandGallery">
                <VisuallyHidden as="h1">{translate("Explore available brands")}</VisuallyHidden>
                {fields.title && (
                    <StyledWrapper {...styles.titleViewAll}>
                        {fields.title && (
                            <Typography
                                {...styles.title}
                                css={css`
                                    font-weight: ${fields.boldTitle ? "600" : "normal"};
                                    text-transform: ${fields.uppercaseTitle ? "uppercase" : "initial"};
                                    letter-spacing: 0.3px;
                                    margin-bottom: 0;
                                `}
                                data-test-selector="brandCategoriesTitle"
                            >
                                {fields.title}
                            </Typography>
                        )}
                    </StyledWrapper>
                )}
                {/* XNG-Changes : Use StyledWrapper instead of FlexWrapContainer and FlexItem */}
                <StyledWrapper {...styles.imageContainer} {...{ mobile, tablet, desktop }}>
                    {brandList.map(brand => (
                        <StyledWrapper key={brand.id} {...styles.imageItem}>
                            {brand === brandList[0] && (
                                <SkipNav
                                    extendedStyles={styles.skipToContent}
                                    text={translate("Skip to full brand list")}
                                    focusFunction={focusFunction}
                                />
                            )}
                            <Clickable href={brand.detailPagePath} {...styles.link}>
                                <LazyImage
                                    imgProps={imgProps}
                                    src={brand.logoSmallImagePath}
                                    altText={`${translate("Go to brand page")} ${brand.logoAltText || brand.name}`}
                                    {...styles.image}
                                />
                            </Clickable>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

export const definition: WidgetDefinition<CustomFieldDefinition> = {
    group: "Brands" as any,
    icon: "image",
    displayName: "Brand Gallery",
    // XNG-Changes: remove allowedContexts
    // allowedContexts: ["BrandsPage", "HomePage"],
    fieldDefinitions: [
        {
            name: fields.title,
            displayName: "Title",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Contextual",
        },
        {
            name: fields.uppercaseTitle,
            displayName: "Uppercase Title",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
        },
        {
            name: fields.boldTitle,
            displayName: "Bold Title",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
        },
        {
            name: fields.brandsToDisplay,
            displayName: "Brands to display",
            editorTemplate: "RadioButtonsField",
            options: [
                {
                    displayName: "Random",
                    value: "random",
                },
                {
                    displayName: "Select brands",
                    value: "select-brands",
                },
            ],
            defaultValue: "random",
            horizontal: true,
            fieldType: "Contextual",
            tooltip: "Brand logos can be picked at random on page load, or you can select specific brands to display.",
        },
        {
            name: fields.numberOfBrandsToDisplay,
            displayName: "Number of brands to display",
            editorTemplate: "DropDownField",
            defaultValue: "12",
            options: [
                { displayName: "1", value: "1" },
                { displayName: "2", value: "2" },
                { displayName: "3", value: "3" },
                { displayName: "4", value: "4" },
                { displayName: "5", value: "5" },
                { displayName: "6", value: "6" },
                { displayName: "7", value: "7" },
                { displayName: "8", value: "8" },
                { displayName: "9", value: "9" },
                { displayName: "10", value: "10" },
                { displayName: "11", value: "11" },
                { displayName: "12", value: "12" },
                { displayName: "13", value: "13" },
                { displayName: "14", value: "14" },
                { displayName: "15", value: "15" },
                { displayName: "16", value: "16" },
                { displayName: "17", value: "17" },
                { displayName: "18", value: "18" },
            ],
            fieldType: "Contextual",
            isVisible: (item: any) => item.fields.brandsToDisplay === "random",
        },
        {
            name: fields.brandsSelected,
            displayName: "Select brands",
            editorTemplate: "CustomSelectBrandsField",
            isVisible: (item: any) => item.fields.brandsToDisplay === "select-brands",
            defaultValue: [],
            fieldType: "Contextual",
        },
        {
            name: fields.maximumNumberOfBrandsPerRow,
            displayName: "Maximum brands per row",
            editorTemplate: "DropDownField",
            defaultValue: "7",
            options: [
                { displayName: "1", value: "1" },
                { displayName: "2", value: "2" },
                { displayName: "3", value: "3" },
                { displayName: "4", value: "4" },
                { displayName: "5", value: "5" },
                { displayName: "6", value: "6" },
                { displayName: "7", value: "7" },
            ],
            fieldType: "Contextual",
            tooltip:
                "Up to 7 brands can display per row. This number should be adjusted based on the number of brands you choose to display. For example, if you display 10 brands you likely want 5 to display per row. Logos will also stack responsively for smaller screen sizes.",
        },
        {
            name: fields.maximumBrandImageHeight,
            displayName: "Maximum brand image height",
            editorTemplate: "IntegerField",
            defaultValue: 100,
            fieldType: "Contextual",
            tooltip:
                "By default, images will scale to meet the width of the screen for the number of brands to display per row. The maximum height value can be used to scale brand logos to be smaller. Default value is 100 pixels.",
        },
    ] as any,
};

const widgetModule: WidgetModule<CustomFieldDefinition> = {
    component: connect(mapStateToProps, mapDispatchToProps)(BrandGallery),
    definition,
};

export default widgetModule;
